import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAppContext } from '../../../../context/AppContext';

export default function CoverLetterMenu({
  isCollapsed,
  coverExpanded,
  theme,
  isMobile,
}) {
  const navigate = useNavigate();
  const {
    coverLetters = [],
    handleDeleteCoverLetter,
    handleRenameCoverLetter: handleRenameCoverLetterContext,
    handleDuplicateCoverLetter: handleDuplicateCoverLetterContext,
  } = useAppContext();

  const { clId } = useParams();
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  const isDark = theme === 'dark';
  const sidebarBorder = isDark ? '#1F1F24' : '#E5E7EB';

  const menuItemStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '6px 12px',
    fontSize: '11.5px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: isDark ? '#F3F4F6' : '#1F2937',
    fontWeight: 500,
    outline: 'none',
  };

  const handleRenameCoverLetter = (letterId, currentName) => {
    const newName = prompt('Enter new cover letter name:', currentName);
    if (!newName || newName.trim() === '') return;
    handleRenameCoverLetterContext(letterId, newName);
  };

  const handleDuplicateCoverLetter = async (letterId) => {
    const dup = await handleDuplicateCoverLetterContext(letterId);
    if (dup) {
      navigate(`/app/cover-letter/${dup.id}/chat`);
    }
  };

  const handleDeleteCoverLetterClick = async (letterId) => {
    const isCurrentlyActive = String(clId) === String(letterId);
    const success = await handleDeleteCoverLetter(letterId);
    if (success && isCurrentlyActive) {
      navigate('/app/dashboard');
    }
  };

  if (isCollapsed || !coverExpanded) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: isMobile ? '0 12px 0 16px' : '0 12px 0 28px',
        marginTop: '2px',
        marginBottom: '6px',
      }}
    >
      {coverLetters.map(cl => {
        const isSelected = String(clId) === String(cl.id);
        const showMenuTrigger = hoveredId === cl.id || activeMenuId === cl.id;
        const displayName = cl.company ? `CL: ${cl.company}` : `Cover Letter #${cl.id.substring(0, 4)}`;

        return (
          <div
            key={cl.id}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
            onMouseEnter={() => setHoveredId(cl.id)}
            onMouseLeave={() => {
              setHoveredId(null);
              setActiveMenuId(null);
            }}
          >
            <Link
              to={`/app/cover-letter/${cl.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '32px',
                padding: '0 10px',
                paddingRight: showMenuTrigger ? '36px' : '10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: isSelected ? 600 : 500,
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                background: isSelected
                  ? (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)')
                  : 'transparent',
                color: isSelected
                  ? (isDark ? '#FFFFFF' : '#111827')
                  : (isDark ? '#9CA3AF' : '#4B5563'),
                transition: 'all 0.1s ease',
                outline: 'none',
                textDecoration: 'none',
                boxSizing: 'border-box',
              }}
              onMouseEnter={e => {
                if (!isSelected) {
                  e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.025)';
                }
              }}
              onMouseLeave={e => {
                if (!isSelected) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <Mail size={13} style={{ marginRight: '8px', color: isSelected ? 'var(--brand)' : (isDark ? '#71717a' : '#9ca3af'), flexShrink: 0 }} />
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {displayName}
              </div>
            </Link>

            {/* Ellipsis Menu Trigger */}
            {showMenuTrigger && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setActiveMenuId(activeMenuId === cl.id ? null : cl.id);
                }}
                style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  border: 'none',
                  background: 'transparent',
                  color: isDark ? '#A1A1AA' : '#6B7280',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  opacity: 0.8,
                  outline: 'none',
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={e => (e.currentTarget.style.opacity = 0.8)}
              >
                •••
              </button>
            )}

            {/* Dropdown Options */}
            {activeMenuId === cl.id && (
              <div
                style={{
                  position: 'absolute',
                  top: '28px',
                  right: '6px',
                  background: isDark ? '#1F1F24' : '#FFFFFF',
                  border: `1px solid ${sidebarBorder}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  width: '100px',
                  padding: '4px 0',
                }}
              >
                <button
                  onClick={() => { setActiveMenuId(null); handleRenameCoverLetter(cl.id, cl.company); }}
                  style={menuItemStyle}
                >
                  Rename
                </button>
                <button
                  onClick={() => { setActiveMenuId(null); handleDuplicateCoverLetter(cl.id); }}
                  style={menuItemStyle}
                >
                  Duplicate
                </button>
                <button
                  onClick={() => {
                    setActiveMenuId(null);
                    handleDeleteCoverLetterClick(cl.id);
                  }}
                  style={{ ...menuItemStyle, color: '#EF4444' }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
